import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Mail, Phone } from "lucide-react";
import { TREATMENTS } from "@/data/treatments";
import { Reveal, EASE } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const URGENCIES = [
  { v: "researching", label: "Just researching" },
  { v: "3-months", label: "Within 3 months" },
  { v: "urgent", label: "Urgent" },
];

const COUNTRIES = [
  "United States", "Canada", "United Kingdom", "Ireland", "Germany", "France",
  "Netherlands", "Spain", "Italy", "Switzerland", "Sweden", "Other",
];

const STEP_TITLES = ["Treatment & urgency", "About you", "Travel window", "Confirm & send"];

const inputCls =
  "w-full border-b border-sand bg-transparent py-3 text-base text-ink placeholder:text-sage transition-colors duration-300 focus:border-brand-green";

export default function QuoteForm() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    treatment: "", urgency: "", full_name: "", email: "",
    phone: "", country: "", travel_dates: "", notes: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validStep = () => {
    if (step === 0) return form.treatment && form.urgency;
    if (step === 1) return form.full_name.trim() && /.+@.+\..+/.test(form.email) && form.country;
    return true;
  };

  const next = () => {
    if (!validStep()) {
      toast.error("Please complete the required fields first.");
      return;
    }
    setStep((s) => Math.min(3, s + 1));
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form, estimated_savings: "" };
      const { data } = await axios.post(`${API}/enquiries`, payload);
      setDone(data);
      toast.success("Enquiry received — our concierge will reach out within 24 hours.");
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="quote" className="scroll-mt-28 bg-cream py-28 sm:py-36" data-testid="quote-section">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <Reveal className="lg:col-span-5">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">Begin your journey</p>
          <h2 className="mt-5 font-serif text-4xl font-medium leading-[1.08] tracking-tight text-brand-green sm:text-5xl">
            Your free, no-obligation quote.
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-ash">
            Four short steps. Within 48 hours a senior care manager responds with a surgeon-reviewed
            plan and one fixed, all-inclusive price.
          </p>
          <ul className="mt-10 space-y-5">
            {[
              "Surgeon reviews your reports — never a salesperson",
              "Fixed package price, valid for 90 days",
              "Zero obligation, zero spam, full confidentiality",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm text-ash">
                <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-brand-green" />
                {item}
              </li>
            ))}
          </ul>
          <div className="mt-12 space-y-3 border-t border-sand pt-8">
            <a href="mailto:help@oraviocare.in" data-testid="quote-email-link" className="flex items-center gap-3 text-sm font-medium text-brand-green transition-colors hover:text-gold">
              <Mail size={16} /> help@oraviocare.in
            </a>
            <a href="tel:+918421559637" data-testid="quote-phone-link" className="flex items-center gap-3 text-sm font-medium text-brand-green transition-colors hover:text-gold">
              <Phone size={16} /> +91 84215 59637 · care line
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-7">
          <div className="rounded-3xl border border-sand bg-white p-8 shadow-[0_30px_80px_rgba(8,61,52,0.1)] sm:p-10" data-testid="quote-form">
            {done ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="flex min-h-[26rem] flex-col items-center justify-center text-center"
                data-testid="quote-success-panel"
              >
                <CheckCircle2 size={56} className="text-brand-green" />
                <h3 className="mt-6 font-serif text-3xl font-semibold text-ink">Request received.</h3>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-ash">
                  Thank you, {done.full_name.split(" ")[0]}. Your dedicated care manager will contact
                  you within 24 hours with your surgeon-reviewed plan.
                </p>
                <p className="mt-6 rounded-full bg-cream px-4 py-2 font-mono text-xs text-brand-green" data-testid="quote-reference-id">
                  Ref · {done.id.slice(0, 8).toUpperCase()}
                </p>
              </motion.div>
            ) : (
              <>
                <div className="flex items-baseline justify-between">
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold">
                    Step 0{step + 1} / 04
                  </p>
                  <p className="font-serif text-xl font-semibold text-ink">{STEP_TITLES[step]}</p>
                </div>
                <div className="mt-4 h-px w-full bg-sand">
                  <motion.div
                    className="h-px bg-gold"
                    animate={{ width: `${((step + 1) / 4) * 100}%` }}
                    transition={{ duration: 0.5, ease: EASE }}
                  />
                </div>

                <div className="mt-8 min-h-[19rem]">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={step}
                      initial={{ opacity: 0, x: 24 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -24 }}
                      transition={{ duration: 0.35, ease: EASE }}
                    >
                      {step === 0 && (
                        <div className="space-y-7">
                          <div>
                            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-sage">Treatment needed</label>
                            <select
                              data-testid="quote-treatment-select"
                              value={form.treatment}
                              onChange={(e) => set("treatment", e.target.value)}
                              className={`${inputCls} mt-2 cursor-pointer`}
                            >
                              <option value="">Select a treatment…</option>
                              {TREATMENTS.map((x) => (
                                <option key={x.id} value={x.name}>{x.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-sage">How soon are you considering treatment?</label>
                            <div className="mt-3 flex flex-wrap gap-3">
                              {URGENCIES.map((u) => (
                                <button
                                  key={u.v}
                                  type="button"
                                  data-testid={`quote-urgency-${u.v}-btn`}
                                  onClick={() => set("urgency", u.v)}
                                  className={`rounded-full border px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
                                    form.urgency === u.v
                                      ? "border-brand-green bg-brand-green text-ivory"
                                      : "border-sand bg-cream text-ash hover:border-brand-green"
                                  }`}
                                >
                                  {u.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {step === 1 && (
                        <div className="grid gap-6 sm:grid-cols-2">
                          <div>
                            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-sage">Full name *</label>
                            <input data-testid="quote-name-input" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Jane Smith" className={`${inputCls} mt-2`} />
                          </div>
                          <div>
                            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-sage">Email *</label>
                            <input data-testid="quote-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@email.com" className={`${inputCls} mt-2`} />
                          </div>
                          <div>
                            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-sage">Phone / WhatsApp</label>
                            <input data-testid="quote-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+1 555 000 1234" className={`${inputCls} mt-2`} />
                          </div>
                          <div>
                            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-sage">Country *</label>
                            <select data-testid="quote-country-select" value={form.country} onChange={(e) => set("country", e.target.value)} className={`${inputCls} mt-2 cursor-pointer`}>
                              <option value="">Select country…</option>
                              {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                        </div>
                      )}

                      {step === 2 && (
                        <div className="space-y-7">
                          <div>
                            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-sage">Preferred travel window</label>
                            <input data-testid="quote-dates-input" value={form.travel_dates} onChange={(e) => set("travel_dates", e.target.value)} placeholder="e.g. October 2026, flexible" className={`${inputCls} mt-2`} />
                          </div>
                          <div>
                            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-sage">Anything your surgeon should know?</label>
                            <textarea data-testid="quote-notes-textarea" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={4} placeholder="Current diagnosis, prior surgeries, mobility concerns…" className={`${inputCls} mt-2 resize-none`} />
                          </div>
                        </div>
                      )}

                      {step === 3 && (
                        <div>
                          <div className="rounded-2xl bg-brand-deep p-7 text-ivory" data-testid="quote-summary-box">
                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Review your request</p>
                            <dl className="mt-5 space-y-3 text-sm">
                              {[
                                ["Treatment", form.treatment],
                                ["Timeline", URGENCIES.find((u) => u.v === form.urgency)?.label || form.urgency],
                                ["Patient", form.full_name],
                                ["Email", form.email],
                                ["Country", form.country],
                                ["Travel window", form.travel_dates || "Flexible"],
                              ].map(([k, v]) => (
                                <div key={k} className="flex justify-between gap-6 border-b border-ivory/10 pb-2.5 last:border-0">
                                  <dt className="text-ivory/50">{k}</dt>
                                  <dd className="text-right font-medium">{v}</dd>
                                </div>
                              ))}
                            </dl>
                          </div>
                          <p className="mt-4 text-xs leading-relaxed text-sage">
                            Our care team responds within 24–48 hours with a surgeon-reviewed plan and
                            your fixed, all-inclusive quote.
                          </p>
                        </div>
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>

                <div className="mt-8 flex items-center justify-between border-t border-cream pt-6">
                  <button
                    type="button"
                    data-testid="quote-back-btn"
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    disabled={step === 0}
                    className="inline-flex items-center gap-2 text-sm font-medium text-ash transition-colors duration-300 enabled:hover:text-brand-green disabled:opacity-30"
                  >
                    <ArrowLeft size={15} /> Back
                  </button>
                  {step < 3 ? (
                    <button
                      type="button"
                      data-testid="quote-next-btn"
                      onClick={next}
                      className="group inline-flex items-center gap-2 rounded-full bg-brand-green px-7 py-3 text-sm font-semibold text-ivory transition-colors duration-300 hover:bg-brand-glow"
                    >
                      Continue <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      data-testid="quote-submit-btn"
                      onClick={submit}
                      disabled={submitting}
                      className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3 text-sm font-semibold text-brand-deep transition-colors duration-300 hover:bg-brand-green hover:text-ivory disabled:opacity-60"
                    >
                      {submitting ? "Sending…" : "Request my fixed quote"}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
